import {useCallback, useEffect, useMemo, useState} from 'react';
import {mapToDeliveryForm} from './utils/map-to-delivery-form';
import {useCreateOrder, useDeliveryOptions} from '@/api/order';
import {ClientOrderAddress, ClientOrderOptions, ClientCreateOrder} from '@/api/types/order';
import {LngLat} from '@yandex/ymaps3-types';
import {debounce} from '@/ui/use-debounced/use-debounce';
import {useCartItems} from '../cartContext';
import {queryClient} from '@/api/http-client/base-client';
import {useMessage} from '../messageContext';
import {useGetCartSummary} from '@/components/cart/utils';
import {useStatus} from '@/api/status';
import {GEOCODE_MAPS_API_KEY} from '@/consts';
import {AxiosError} from 'axios';
import {getIsGuest} from '@/components/login-modal/login-modal';

export const DELIVERY_WORKING_TIME_START = 11;
export const DELIVERY_WORKING_TIME_END = 22;

// Address geocoding utilities
const updateMapPoint = async (street: string, setPoint: (point: LngLat) => void) => {
    if (!street) return;
    const geoData = await fetchGeoData(street);
    if (geoData?.point) {
        setPoint(geoData.point);
    }
};

const debouncedUpdateMapPoint = debounce(updateMapPoint, 2000);
export type AddressForm = ClientOrderAddress;

export type OrderForm = Omit<ClientCreateOrder, 'deliveryTime' | 'address' | 'deliveryPrice'> & {
    address: AddressForm;
    deliveryTime?: number;
    minOrder?: number;
    freeDelivery?: number;
    deliveryPrice?: number;
};

export type OrderFormOptions = Omit<ClientOrderOptions, 'address'> & {
    timeOption?: [number, number] | null;
    polygons?: {
        zoneId: number;
        areaId: number;
        coordinates: [number, number][];
    }[];
    isCorrectZone?: boolean;
};

export const useDeliveryForm = () => {
    const {data: orderOptions, isLoading, error, refetch} = useDeliveryOptions();

    const [hasAddressSelected, setHasAddressSelected] = useState(false);
    const {mutateAsync: createOrderAsync} = useCreateOrder();
    const {} = useStatus();

    const {items} = useCartItems();

    const [{state, options}, setForm] = useState<{
        state: OrderForm | null;
        options: OrderFormOptions | null;
    }>({state: null, options: null});

    const messageApi = useMessage();
    const {productsSum} = useGetCartSummary(state?.items ?? []);

    const [point, setPoint] = useState<LngLat>(DEFAULT_COORDINATES);
    // Field updaters
    const updateField = useCallback(
        <K extends keyof OrderForm>(field: K, value: OrderForm[K]) => {
            setForm((prev) => (prev.state ? {...prev, state: {...prev.state, [field]: value}} : {...prev}));
            if (field === 'deliveryType') {
                if (value === 'delivery' && state?.address?.street) {
                    updateMapPoint(state?.address.street, setPoint);
                } else {
                    setPoint(DEFAULT_COORDINATES);
                }
            }
        },
        [state?.address?.street, setPoint],
    );

    const updateAddressField = useCallback((name: string, value: string | number | boolean) => {
        setForm((prev) => {
            if (!prev.state) return prev;

            return {
                ...prev,
                state: {
                    ...prev.state,
                    address: {
                        ...prev.state.address,
                        [name]: value,
                    },
                },
            };
        });
    }, []);

    const handleAddressChange = useCallback(
        (
            e:
                | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                | {
                      target: {name: string; value: string; type: string};
                  },
            onStreetChange?: (address: string) => void,
        ) => {
            const {name, value, type} = e.target;
            const parsedValue = type === 'number' ? (e.target as HTMLInputElement).valueAsNumber : value;
            const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

            if (name === 'saveAddress' && type === 'checkbox') {
                updateField('saveAddress', !!checked);
                return;
            }

            const newValue = type === 'checkbox' ? !!checked : parsedValue;
            updateAddressField(name, newValue);

            if (name === 'street' && state?.address?.street !== value) {
                onStreetChange?.(value);
            }
        },
        [updateField, updateAddressField, state?.address?.street],
    );

    const handleAddressInputChange = useCallback(
        (e: Parameters<typeof handleAddressChange>[0]) => {
            handleAddressChange(e, (point) => debouncedUpdateMapPoint(point, setPoint));
        },
        [handleAddressChange, setPoint],
    );

    const isGuest = getIsGuest();
    useEffect(() => {
        if (orderOptions && items && !state && !options) {
            if (isGuest) {
                const optionsWithoutAddress = {...orderOptions, addresses: []};
                const {initialState: state, options} = mapToDeliveryForm(optionsWithoutAddress, items);
                setForm({state, options});
                return;
            }

            const {initialState: state, options} = mapToDeliveryForm(orderOptions, items);
            setForm({state, options});
            handleAddressInputChange({target: {value: state.address.street, name: 'street', type: 'text'}});
        }
    }, [orderOptions, items, handleAddressInputChange, options, state, isGuest]);

    const handlePointChange = useCallback(
        (coords: LngLat) => {
            if (!coords) return;

            setPoint(coords);
            fetchGeoData(coords).then((geoData) => {
                if (geoData?.addressText && geoData.addressText !== state?.address?.street) {
                    updateAddressField('street', geoData.addressText);
                }
            });
        },
        [state?.address?.street, updateAddressField],
    );

    const onZoneSelect = useCallback(
        (id?: number) => {
            const zone = orderOptions?.zones.find((val) => val.id === id);
            if (!zone || !id) {
                messageApi.error('Выберите точку из доступных зон для доставки');
                setForm((prev) =>
                    prev.state && prev.options ? {...prev, options: {...prev.options, isCorrectZone: false}} : prev,
                );
                return;
            }

            const hasDeliveryTime =
                (zone.availableTime &&
                    state?.deliveryTime &&
                    state?.deliveryTime < zone?.availableTime[1] &&
                    state?.deliveryTime > zone?.availableTime[0]) ||
                (state?.deliveryTime &&
                    new Date(state?.deliveryTime * 1000).toDateString() !== new Date(Date.now()).toDateString());

            const currentDate = new Date(Date.now());
            currentDate.setDate(currentDate.getDate() + 1);
            currentDate.setHours(DELIVERY_WORKING_TIME_START, 0, 0, 0);

            const deliveryTime = hasDeliveryTime ? state?.deliveryTime : zone.availableTime?.[0];

            setForm((prev) =>
                prev.state && prev.options
                    ? {
                          options: {
                              ...prev.options,
                              timeOption: zone.availableTime,
                              isCorrectZone: true,
                          },
                          state: {
                              ...prev.state,
                              deliveryTime: deliveryTime ?? +currentDate / 1000,
                              minOrder: zone?.minOrder,
                              freeDelivery: zone?.freeDelivery,
                              deliveryPrice: zone?.deliveryPrice,
                          },
                      }
                    : prev,
            );
        },
        [state?.deliveryTime, orderOptions?.zones, messageApi],
    );

    const polygons = useMemo(
        () =>
            options?.zones.flatMap((zone) =>
                zone.areas.map((area) => ({
                    zoneId: zone.id,
                    areaId: area.id,
                    coordinates: area.coords,
                })),
            ),
        [options?.zones],
    );

    const handleAddressSelected = useCallback(async () => {
        if (!state) return null;

        await queryClient.refetchQueries({queryKey: ['status']});

        const freshStatus = queryClient.getQueryData(['status']);

        if (!freshStatus) {
            messageApi.error('Доставка через сайт в данный момент недоступна');
            return null;
        }

        if (isGuest && !state.courierComment) {
            messageApi.error('Введите ваш номер телефона');
            return null;
        }

        if (state.deliveryType === 'delivery' && !state.address.street) {
            messageApi.error('Заполните адрес');
            return null;
        }
        if (state.deliveryType === 'delivery' && !options?.isCorrectZone) {
            messageApi.error('Выберите адрес из зоны доставки');
            return null;
        }

        if (state.deliveryType === 'delivery' && state.minOrder && state.minOrder > productsSum) {
            messageApi.error(`Минимальная цена заказа ${state.minOrder}`);
            return null;
        }
        setHasAddressSelected(true);
    }, [isGuest, messageApi, options?.isCorrectZone, productsSum, state]);

    useEffect(() => {
        let foundZoneId: number | undefined = undefined;
        if (!polygons) {
            return;
        }
        for (const polygon of polygons) {
            if (pointInPolygon([point[1], point[0]], polygon.coordinates)) {
                foundZoneId = polygon.zoneId;
                break;
            }
        }
        onZoneSelect(foundZoneId);
    }, [point, polygons, onZoneSelect]);

    const handleCreateOrder = useCallback(async () => {
        try {
            if (!state) return null;

            await queryClient.refetchQueries({queryKey: ['status']});

            const freshStatus = queryClient.getQueryData(['status']);

            if (!freshStatus) {
                messageApi.error('Доставка через сайт в данный момент недоступна');
                return null;
            }

            if (!state.deliveryTime) {
                messageApi.error('Не выбрано время');
                return null;
            }
            const date = new Date(state.deliveryTime * 1000);
            if (
                date.getHours() < DELIVERY_WORKING_TIME_START ||
                date.getHours() > DELIVERY_WORKING_TIME_END ||
                (date.getHours() === DELIVERY_WORKING_TIME_END && date.getMinutes() > 0)
            ) {
                messageApi.error(`Доставка работает с ${DELIVERY_WORKING_TIME_START} до ${DELIVERY_WORKING_TIME_END}`);
                return null;
            }

            if (state.deliveryPrice === undefined) {
                messageApi.error('Что-то пошло не так');
                return null;
            }

            const calculatedDeliveryPrice =
                state.freeDelivery && state.freeDelivery < productsSum ? 0 : state.deliveryPrice;

            const order: ClientCreateOrder = {
                ...state,
                deliveryPrice: state.deliveryType === 'delivery' ? calculatedDeliveryPrice : 0,
                deliveryTime: state.deliveryTime,
            };

            const paymentUrl = await createOrderAsync(order);
            return paymentUrl;
        } catch (error) {
            messageApi.error(`Ошибка при создании заказа ${error}`);
            return null;
        }
    }, [state, messageApi, createOrderAsync, productsSum]);

    // Loading and error states
    if (isLoading) {
        return {
            isLoading: true,
            state: null,
            options: null,
            handlers: null,
            point,
            setPoint: () => {},
        };
    }

    if (error) {
        return {
            refetch,
            isLoading: false,
            error: error as AxiosError,
            state: null,
            options: null,
            handlers: null,
            point,
            setPoint: () => {},
        };
    }

    return {
        isLoading: false,
        error: undefined,
        state,
        options: options && {...options, polygons},
        handlers: {
            updateField,
            handleAddressChange: handleAddressInputChange,
            handleCreateOrder,
        },
        point,
        setPoint: handlePointChange,
        onZoneSelect,
        hasAddressSelected,
        handleAddressSelected,
    };
};

const GEOCODING_URL = `https://geocode-maps.yandex.ru/1.x/?apikey=${GEOCODE_MAPS_API_KEY}&format=json&lang=ru_RU`;
const DEFAULT_COORDINATES: LngLat = [84.925281, 56.444593];

const fetchGeoData = async (query: string | LngLat) => {
    try {
        const url =
            typeof query === 'string'
                ? `${GEOCODING_URL}&geocode=Томск, ${query}`
                : `${GEOCODING_URL}&geocode=${query}`;
        const response = await fetch(url);
        const data = await response.json();

        const featureMember = data.response.GeoObjectCollection.featureMember?.[0];
        if (!featureMember) return null;

        const point = featureMember.GeoObject.Point.pos.split(' ').map(Number) as LngLat;
        const addressText = featureMember.GeoObject.metaDataProperty.GeocoderMetaData.text;

        return {point, addressText};
    } catch (error) {
        console.error('Geocoding error:', error);
        return null;
    }
};

// Функция для проверки точки в полигоне (алгоритм ray-casting)
const pointInPolygon = (point: [number, number], polygon: [number, number][]) => {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];

        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
    }

    return inside;
};
